// src/components/RoomRow.js
import React from "react";
import {
  Box,
  Collapse,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
  ClickAwayListener,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import SquareFootIcon from "@mui/icons-material/SquareFoot";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CategoryIcon from "@mui/icons-material/Category";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  textFieldSx,
  formatCoords,
  formatArea,
  formatAreaInput,
  coordsToJSON,
  metersToHundredths,
  toInt,
  toFloat,
  handleCoordinateChange,
  handleCoordinateBlur,
} from "./utils/tableHelpers";
import { useInternalToggle } from "../hooks/useToggle";

// Field info configuration - easy to update descriptions
const FIELD_INFO = {
  room_name:
    "The display name for this room that will be shown throughout the system.",
  room_type:
    "Select the category that best describes this room's purpose (e.g., Office, Bathroom, Kitchen).",
  room_area:
    "Total floor area as shown on the blueprint of the room in square meters.",
  room_cleaning_area:
    "The total cleaning area of the room in square meters, excluding furniture and obstacles.",
  room_baseline_cleaning_area:
    "This is 85% of the total cleaning area, representing the baseline cleaning requirement for the room.",
  room_description:
    "Additional details about the room, special requirements, or notes about the furniture quantity and type.",
  room_restriction: "Restrictions on the service assistant for this room.",
  entrance_coordinates:
    "GPS coordinates (latitude, longitude) of the room's entrance point for navigation.",
  access_features:
    "Accessibility features available: lift access, automatic doors, and ramps/slopes.",
  threshold_info:
    "Type and height of door threshold that robots need to navigate when entering.",
  floor_type:
    "Material type of the flooring which affects cleaning methods and robot behavior.",
  quality_profile:
    "Cleaning standard level required for this room (e.g., Standard, High, Medical).",
  soil_level:
    "Expected dirt/contamination level that determines cleaning intensity.",
  cleaning_scores:
    "Performance scores (0-8 scale) for different cleaning tasks in this room type.",
  notes:
    "Additional operational notes, special instructions, or maintenance reminders.",
};

// Reusable InfoTooltip component
function InfoTooltip({ fieldKey, children }) {
  const [open, setOpen] = React.useState(false);
  const description = FIELD_INFO[fieldKey];

  if (!description) return children;

  const handleTooltipToggle = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {children}
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <Tooltip
          title={description}
          open={open}
          onClose={handleTooltipClose}
          placement="top"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "#1976d2",
                color: "white",
                fontSize: "0.8rem",
                maxWidth: 300,
                padding: "8px 12px",
                "& .MuiTooltip-arrow": {
                  color: "#1976d2",
                },
              },
            },
          }}
        >
          <IconButton
            size="small"
            onClick={handleTooltipToggle}
            sx={{
              p: 0.25,
              ml: 0.5,
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.1)",
              },
            }}
          >
            <InfoOutlinedIcon
              sx={{
                fontSize: 14,
                color: "#1976d2",
                opacity: 0.7,
                "&:hover": { opacity: 1 },
              }}
            />
          </IconButton>
        </Tooltip>
      </ClickAwayListener>
    </Box>
  );
}

export default function RoomRow({
  room,
  onSaveRoom,
  onCancel,
  forceEdit = false,
  roomTypes = [],
  floorTypes = [],
}) {
  const { isOpen, toggle } = useInternalToggle(false);
  const [editing, setEditing] = React.useState(forceEdit);
  const [editData, setEditData] = React.useState(room);

  React.useEffect(() => setEditData(room), [room]);
  React.useEffect(() => {
    if (forceEdit) setEditing(true);
  }, [forceEdit]);

  const handleEdit = () => setEditing(true);
  const handleCancel = () => {
    if (onCancel && room.isNew) {
      onCancel(); // Remove temporary room
    } else {
      setEditData(room);
      setEditing(false);
    }
  };
  const handleSave = () => {
    const dataToSave = {
      ...editData,
      room_entrance_coordinates: coordsToJSON(
        editData.room_entrance_coordinates,
      ),
    };
    onSaveRoom?.(dataToSave);
    setEditing(false);
  };
  const handleChange = (field, value) =>
    setEditData((prev) => ({ ...prev, [field]: value }));

  return (
    <>
      <TableRow
        hover
        onClick={toggle}
        sx={{
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <TableCell padding="checkbox">
          <IconButton size="small">
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title={editData.room_name || ""}>
              <span
                style={{
                  display: "inline-block",
                  maxWidth: 360,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {editData.room_name || "Unnamed room"}
              </span>
            </Tooltip>
            {roomTypes.find((t) => t.pk_id === editData.room_type_id)
              ?.type_name && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <CategoryIcon sx={{ fontSize: 16, color: "#666" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {
                    roomTypes.find((t) => t.pk_id === editData.room_type_id)
                      ?.type_name
                  }
                </Typography>
              </Box>
            )}
            {editData.room_area && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <SquareFootIcon sx={{ fontSize: 16, color: "#1a45a1" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {formatArea(editData.room_area)}
                </Typography>
              </Box>
            )}
            {editData.room_cleaning_area && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <CleaningServicesIcon sx={{ fontSize: 16, color: "#2b6e2f" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {formatArea(editData.room_cleaning_area)}
                </Typography>
              </Box>
            )}
            {editData.room_baseline_cleaning_area && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}
              >
                <CleaningServicesIcon sx={{ fontSize: 16, color: "#b32a2a" }} />
                <Typography variant="caption" sx={{ color: "#666" }}>
                  {formatArea(editData.room_baseline_cleaning_area)}
                </Typography>
              </Box>
            )}
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 6, my: 1, marginLeft: 0 }}>
              <Card
                variant="outlined"
                sx={{ mb: 2, backgroundColor: "#f9fafb" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary">
                      Room Details
                    </Typography>
                    {!editing ? (
                      <IconButton size="small" onClick={handleEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    ) : (
                      <Box>
                        <IconButton
                          size="small"
                          onClick={handleSave}
                          color="success"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancel}
                          color="error"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {!editing ? (
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="room_name">
                          <Typography variant="caption" color="text.secondary">
                            Name:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {editData.room_name || "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 3 }}>
                        <InfoTooltip fieldKey="room_type">
                          <Typography variant="caption" color="text.secondary">
                            Room Type:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {roomTypes.find(
                            (t) => t.pk_id === editData.room_type_id,
                          )?.type_name ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 3, md: 3 }}>
                        <InfoTooltip fieldKey="room_restriction">
                          <Typography variant="caption" color="text.secondary">
                            Room Restriction:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {editData.room_restriction || "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 5, md: 3 }}>
                        <InfoTooltip fieldKey="room_area">
                          <Typography variant="caption" color="text.secondary">
                            Area:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {formatArea(editData.room_area)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 5, md: 3 }}>
                        <InfoTooltip fieldKey="room_cleaning_area">
                          <Typography variant="caption" color="text.secondary">
                            Cleaning Area:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {formatArea(editData.room_cleaning_area)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4, md: 3 }}>
                        <InfoTooltip fieldKey="room_baseline_cleaning_area">
                          <Typography variant="caption" color="text.secondary">
                            Baseline Cleaning Area:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {formatArea(editData.room_baseline_cleaning_area)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <InfoTooltip fieldKey="room_description">
                          <Typography variant="caption" color="text.secondary">
                            Description:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {editData.room_description || "-"}
                        </Typography>
                      </Grid>

                      {/* Access and Threshold */}
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Divider
                          sx={{
                            my: 2,
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            borderBottomWidth: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ mb: 2 }}
                        >
                          Access & Threshold
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <InfoTooltip fieldKey="entrance_coordinates">
                          <Typography variant="caption" color="text.secondary">
                            Entrance Coords:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {formatCoords(
                            editData.room_entrance_coordinates ||
                              room.room_entrance_coordinates,
                          )}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 5 }}>
                        <InfoTooltip fieldKey="access_features">
                          <Typography variant="caption" color="text.secondary">
                            Access:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          Lift: {editData.lift_access ? "Yes" : "No"} · Auto
                          Door: {editData.automatic_door ? "Yes" : "No"} ·
                          Slope: {editData.slope ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6, md: 3 }}>
                        <InfoTooltip fieldKey="threshold_info">
                          <Typography variant="caption" color="text.secondary">
                            Threshold:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {editData.threshold_type ?? "-"}{" "}
                          {editData.threshold_height != null
                            ? `(${editData.threshold_height} cm)`
                            : ""}
                        </Typography>
                      </Grid>

                      {/* Floor & Quality Section */}
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Divider
                          sx={{
                            my: 2,
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            borderBottomWidth: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ mb: 2 }}
                        >
                          Floor & Quality
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="floor_type">
                          <Typography variant="caption" color="text.secondary">
                            Floor Type:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {floorTypes.find(
                            (t) => t.pk_id === editData.floor_type_id,
                          )?.type_name ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="quality_profile">
                          <Typography variant="caption" color="text.secondary">
                            Quality Profile:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {editData.quality_profile ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="soil_level">
                          <Typography variant="caption" color="text.secondary">
                            Soil Level:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {editData.soil_level ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoTooltip fieldKey="cleaning_scores">
                          <Typography variant="caption" color="text.secondary">
                            Cleaning Scores (0-8):
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          Dry Mop: {editData.dry_mop ?? "-"} · Floor Wash:{" "}
                          {editData.floor_washing ?? "-"} · Fixtures:{" "}
                          {editData.fixtures ?? "-"}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoTooltip fieldKey="notes">
                          <Typography variant="caption" color="text.secondary">
                            Notes:
                          </Typography>
                        </InfoTooltip>
                        <Typography variant="body2">
                          {editData.notes || "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      {/* Editable form - see above for details */}
                      {/* Room Details Section */}
                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="room_name">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Name:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          size="small"
                          value={editData.room_name || ""}
                          onChange={(e) =>
                            handleChange("room_name", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 4, md: 3 }}>
                        <InfoTooltip fieldKey="room_type">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Room Type:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          select
                          size="small"
                          value={editData.room_type_id || ""}
                          onChange={(e) =>
                            handleChange("room_type_id", toInt(e.target.value))
                          }
                          fullWidth
                          sx={textFieldSx}
                        >
                          <MenuItem value="">Select Room Type</MenuItem>
                          {roomTypes.map((type) => (
                            <MenuItem key={type.pk_id} value={type.pk_id}>
                              {type.type_name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 4, md: 3 }}>
                        <InfoTooltip fieldKey="room_restriction">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Room Restriction:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          select
                          size="small"
                          value={editData.room_restriction || ""}
                          onChange={(e) =>
                            handleChange(
                              "room_restriction",
                              e.target.value || null,
                            )
                          }
                          fullWidth
                          sx={textFieldSx}
                        >
                          <MenuItem value="">Select Room Restriction</MenuItem>

                          <MenuItem value={"none"}>None</MenuItem>
                          <MenuItem value={"male"}>Male</MenuItem>
                          <MenuItem value={"female"}>Female</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 4, md: 2 }}>
                        <InfoTooltip fieldKey="room_area">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Area (m²):
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          size="small"
                          type="number"
                          value={formatAreaInput(editData.room_area)}
                          onChange={(e) =>
                            handleChange(
                              "room_area",
                              metersToHundredths(e.target.value),
                            )
                          }
                          inputProps={{ step: "0.01", min: 0 }}
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 4, md: 3 }}>
                        <InfoTooltip fieldKey="room_cleaning_area">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Cleaning Area (m²):
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          size="small"
                          type="number"
                          value={formatAreaInput(editData.room_cleaning_area)}
                          onChange={(e) =>
                            handleChange(
                              "room_cleaning_area",
                              metersToHundredths(e.target.value),
                            )
                          }
                          inputProps={{ step: "0.01", min: 0 }}
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      <Grid size={{ xs: 4, md: 3 }}>
                        <InfoTooltip fieldKey="room_baseline_cleaning_area">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Baseline Cleaning Area (m²):
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          size="small"
                          type="number"
                          value={formatAreaInput(
                            editData.room_baseline_cleaning_area,
                          )}
                          onChange={(e) =>
                            handleChange(
                              "room_baseline_cleaning_area",
                              metersToHundredths(e.target.value),
                            )
                          }
                          inputProps={{ step: "0.01", min: 0 }}
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 12 }}>
                        <InfoTooltip fieldKey="room_description">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Description:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          size="small"
                          multiline
                          rows={2}
                          value={editData.room_description || ""}
                          onChange={(e) =>
                            handleChange("room_description", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      {/* Access & Threshold Section */}
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Divider
                          sx={{
                            my: 2,
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            borderBottomWidth: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ mb: 2 }}
                        >
                          Access & Threshold
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 4, md: 3 }}>
                        <InfoTooltip fieldKey="entrance_coordinates">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Entrance Coords (lat, lon):
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          size="small"
                          placeholder="lat, lon"
                          defaultValue={formatCoords(
                            room.room_entrance_coordinates,
                          )}
                          onChange={(e) => {
                            handleCoordinateChange(e.target.value, (value) =>
                              handleChange("room_entrance_coordinates", value),
                            );
                          }}
                          onBlur={(e) => {
                            handleCoordinateBlur(e.target.value, (value) =>
                              handleChange("room_entrance_coordinates", value),
                            );
                          }}
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 9 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Access:
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!editData.lift_access}
                              onChange={(e) =>
                                handleChange(
                                  "lift_access",
                                  e.target.checked ? 1 : 0,
                                )
                              }
                              size="small"
                            />
                          }
                          label="Lift access"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!editData.automatic_door}
                              onChange={(e) =>
                                handleChange(
                                  "automatic_door",
                                  e.target.checked ? 1 : 0,
                                )
                              }
                              size="small"
                            />
                          }
                          label="Automatic door"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!editData.slope}
                              onChange={(e) =>
                                handleChange("slope", e.target.checked ? 1 : 0)
                              }
                              size="small"
                            />
                          }
                          label="Slope"
                        />
                      </Grid>

                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Threshold Type:
                        </Typography>
                        <TextField
                          select
                          size="small"
                          value={editData.threshold_type || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleChange("threshold_type", value);
                            // Automatically set threshold height to 0 when "flat" is selected
                            if (value === "flat") {
                              handleChange("threshold_height", 0);
                            }
                          }}
                          fullWidth
                          sx={textFieldSx}
                        >
                          {["staircase", "step", "flat", "ramp", "other"].map(
                            (t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ),
                          )}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 6, md: 3 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Threshold Height (cm):
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={editData.threshold_height ?? ""}
                          onChange={(e) =>
                            handleChange(
                              "threshold_height",
                              toFloat(e.target.value),
                            )
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>

                      {/* Floor & Quality Section */}
                      <Grid size={{ xs: 12, md: 12 }}>
                        <Divider
                          sx={{
                            my: 2,
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            borderBottomWidth: 1,
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          sx={{ mb: 2 }}
                        >
                          Floor & Quality
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="floor_type">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Floor Type:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          select
                          size="small"
                          value={editData.floor_type_id || ""}
                          onChange={(e) =>
                            handleChange("floor_type_id", toInt(e.target.value))
                          }
                          fullWidth
                          sx={textFieldSx}
                        >
                          <MenuItem value="">Select Floor Type</MenuItem>
                          {floorTypes.map((type) => (
                            <MenuItem key={type.pk_id} value={type.pk_id}>
                              {type.type_name}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="quality_profile">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Quality Profile:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          select
                          size="small"
                          value={editData.quality_profile || ""}
                          onChange={(e) =>
                            handleChange("quality_profile", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        >
                          {["1", "2", "3", "4", "inactive", "unassigned"].map(
                            (q) => (
                              <MenuItem key={q} value={q}>
                                {q}
                              </MenuItem>
                            ),
                          )}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 4, md: 4 }}>
                        <InfoTooltip fieldKey="soil_level">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Soil Level:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          select
                          size="small"
                          value={editData.soil_level || ""}
                          onChange={(e) =>
                            handleChange("soil_level", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        >
                          {["low", "medium", "high"].map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid size={{ xs: 12, md: 12 }}>
                        <InfoTooltip fieldKey="cleaning_scores">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Cleaning Scores (0-8):
                          </Typography>
                        </InfoTooltip>
                        <Box sx={{ display: "flex", gap: 3 }}>
                          <TextField
                            select
                            size="small"
                            label="Dry Mop"
                            value={editData.dry_mop ?? ""}
                            onChange={(e) =>
                              handleChange("dry_mop", toInt(e.target.value))
                            }
                            sx={{ width: 120, ...textFieldSx }}
                          >
                            <MenuItem value="">Select</MenuItem>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((score) => (
                              <MenuItem key={score} value={score}>
                                {score}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label="Floor Wash"
                            value={editData.floor_washing ?? ""}
                            onChange={(e) =>
                              handleChange(
                                "floor_washing",
                                toInt(e.target.value),
                              )
                            }
                            sx={{ width: 140, ...textFieldSx }}
                          >
                            <MenuItem value="">Select</MenuItem>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((score) => (
                              <MenuItem key={score} value={score}>
                                {score}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label="Fixtures"
                            value={editData.fixtures ?? ""}
                            onChange={(e) =>
                              handleChange("fixtures", toInt(e.target.value))
                            }
                            sx={{ width: 120, ...textFieldSx }}
                          >
                            <MenuItem value="">Select</MenuItem>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((score) => (
                              <MenuItem key={score} value={score}>
                                {score}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, md: 12 }}>
                        <InfoTooltip fieldKey="notes">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mb: 0.5, display: "block" }}
                          >
                            Notes:
                          </Typography>
                        </InfoTooltip>
                        <TextField
                          size="small"
                          multiline
                          rows={3}
                          value={editData.notes || ""}
                          onChange={(e) =>
                            handleChange("notes", e.target.value)
                          }
                          fullWidth
                          sx={textFieldSx}
                        />
                      </Grid>
                      {editing && (
                        <Grid size={{ xs: 12, md: 12 }}>
                          <Box
                            sx={{
                              flexDirection: "row",
                              display: "flex",
                              justifyContent: "right",
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={handleSave}
                              color="success"
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancel}
                              color="error"
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
